// Resource / job status notices — distinct from conversational bot bubbles and step fiches.
//
// Card structure:
//   top-left label  : resource + operation  (CHECK RECEPTOR, RECEPTOR PREPARATION, DOCKING…)
//   top-right pill  : status value (completed / in_progress / failed…)
//   headline        : short sentence (Récepteur déjà en session, Préparation du récepteur lancée…)
//   rows            : PDB, Statut, Action, Séquence… — never IDs
//
// Color driven by status, not operation kind:
//   success  → green   (completed / reuse / delete ok)
//   warning  → orange  (in_progress / pending)
//   danger   → red     (failed / error / cancelled)
const STEP_BLOCK_RE = /^Step\s+\d+\s+—\s+([^\n]+)\n+([\s\S]+)$/i;
// IDs the user never sees in the MexaScreen panel — strip them from cards.
const ID_LABELS = new Set([
    'receptor id', 'peptide id', 'docking id', 'screening id',
    'binding site id', 'resource',
]);
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function normalizeStatus(raw) {
    return (raw === null || raw === void 0 ? void 0 : raw.trim()) || undefined;
}
function filterRows(rows) {
    return rows.filter(r => !ID_LABELS.has(r.label.toLowerCase()));
}
// ── Color class — driven by status / kind ────────────────────────────────────
function noticeColorClass(notice) {
    var _a;
    const s = ((_a = notice.status) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const k = notice.kind;
    if (k === 'error' || ['failed', 'error', 'job_submission_failed', 'cancelled'].includes(s)) {
        return 'chat-resource-notice-danger';
    }
    if (k === 'pending' || ['in_progress', 'pending', 'running'].includes(s)) {
        return 'chat-resource-notice-warning';
    }
    return 'chat-resource-notice-success';
}
// ── Resource + action label (top-left of each card) ──────────────────────────
export function resourceActionLabel(notice) {
    const { resource: r, kind: k } = notice;
    if (r === 'receptor') {
        if (k === 'check')
            return 'STATUT DU RÉCEPTEUR';
        if (k === 'delete')
            return 'DELETE RECEPTOR';
        return 'RECEPTOR PREPARATION';
    }
    if (r === 'peptide') {
        if (k === 'check')
            return 'STATUT DU DESIGN';
        if (k === 'delete')
            return 'DELETE PEPTIDE';
        return 'PEPTIDE DESIGN';
    }
    if (r === 'docking') {
        if (k === 'check')
            return 'STATUT DU DOCKING';
        if (k === 'delete')
            return 'DELETE DOCKING';
        return 'DOCKING';
    }
    if (r === 'binding_site') {
        if (k === 'check')
            return 'STATUT DU BINDING SITE';
        return 'BINDING SITE';
    }
    return 'INFO';
}
/** Strip legacy "contrôle" headlines from LangGraph; check cards use rows only. */
function sanitizeHeadline(notice) {
    if (notice.kind === 'check')
        return '';
    const h = notice.headline.trim();
    if (!h)
        return '';
    if (/contrôle|résultat\s+du\s+check|ci-dessus/i.test(h))
        return '';
    return h;
}
/** Short intro bubble shown before notice cards (replaces redundant ai_response). */
export function noticeIntroMessage(notices) {
    if (!notices.length)
        return null;
    const n = notices[0];
    if (n.kind === 'check') {
        switch (n.resource) {
            case 'receptor': return 'Voici le statut actuel du récepteur.';
            case 'peptide': return 'Voici le statut du design du peptide.';
            case 'docking': return 'Voici le statut du docking.';
            case 'binding_site': return 'Voici le statut du binding site.';
            default: return 'Voici les résultats du check.';
        }
    }
    if (n.kind === 'reuse') {
        switch (n.resource) {
            case 'receptor': return 'Ce récepteur est déjà en session — aucune requête n\'a été envoyée.';
            case 'peptide': return 'Ce peptide est déjà en session — aucune requête n\'a été envoyée.';
            case 'docking': return 'Ce docking est déjà en session — aucune requête n\'a été envoyée.';
            default: return 'Élément existant réutilisé — aucune requête n\'a été envoyée.';
        }
    }
    if (n.kind === 'launch') {
        switch (n.resource) {
            case 'receptor': return 'La préparation du récepteur a été lancée.';
            case 'peptide': return 'Le design du peptide a été lancé.';
            case 'docking': return 'Le docking a été lancé.';
            default: return 'Le job a été lancé.';
        }
    }
    if (n.kind === 'delete') {
        switch (n.resource) {
            case 'receptor': return 'Le récepteur a bien été supprimé.';
            case 'peptide': return 'Le peptide a bien été supprimé.';
            case 'docking': return 'Le docking a bien été supprimé.';
            default: return 'Suppression effectuée.';
        }
    }
    if (n.kind === 'pending') {
        return 'Le job est en cours — voici où il en est.';
    }
    if (n.kind === 'error') {
        return 'La requête n\'a pas abouti — voir le détail ci-dessous.';
    }
    return null;
}
// ── Status pill class ─────────────────────────────────────────────────────────
function statusPillClass(status) {
    const s = (status !== null && status !== void 0 ? status : '').toLowerCase();
    if (['completed', 'complete', 'done', 'ok'].includes(s))
        return 'chat-notice-status-done';
    if (['failed', 'error', 'job_submission_failed', 'cancelled'].includes(s))
        return 'chat-notice-status-failed';
    if (['in_progress', 'pending', 'running'].includes(s))
        return 'chat-notice-status-progress';
    return 'chat-notice-status-neutral';
}
// ── Fallback parsers (heuristic on ai_response until agent sends chat_notices) ──
function parseReceptorReuse(_stepTitle, body) {
    const m = body.match(/Récepteur\s+(?:#\d+\s+)?\(([^)]+)\)\s+déjà en session\s+\(statut\s*:\s*([^)]+)\)\s*[—–-]\s*aucune requête envoyée/i);
    if (!m)
        return null;
    return {
        kind: 'reuse',
        resource: 'receptor',
        title: 'RECEPTOR PREPARATION',
        headline: 'Récepteur déjà en session',
        status: normalizeStatus(m[2]),
        rows: filterRows([
            { label: 'PDB', value: m[1].trim() },
            { label: 'Statut', value: m[2].trim() },
            { label: 'Action', value: 'Aucune requête envoyée' },
        ]),
    };
}
function parseReceptorLaunch(_stepTitle, body) {
    if (!/POST\s+.+\/prepare-receptor|Job lancé|Récepteur\s+#?\d*\s*prêt/i.test(body))
        return null;
    const pdbMatch = body.match(/PDB\s*:\s*(\S+)/i);
    const statusMatch = body.match(/statut\s*:\s*([^\n.]+)/i);
    const sitesMatch = body.match(/(\d+)\s+binding site/i);
    const rows = [];
    if (pdbMatch)
        rows.push({ label: 'PDB', value: pdbMatch[1] });
    if (statusMatch)
        rows.push({ label: 'Statut', value: statusMatch[1].trim() });
    if (sitesMatch)
        rows.push({ label: 'Binding sites', value: sitesMatch[1] });
    const failed = /échoué|failed|error/i.test(body);
    const status = normalizeStatus(statusMatch === null || statusMatch === void 0 ? void 0 : statusMatch[1]);
    return {
        kind: failed ? 'error' : 'launch',
        resource: 'receptor',
        title: 'RECEPTOR PREPARATION',
        headline: failed ? 'Échec de la préparation' : 'Préparation du récepteur lancée',
        status,
        rows: rows.length ? rows : [{ label: 'Détail', value: body.split('\n').slice(-2).join(' ').trim() }],
    };
}
function parseResourceCheck(body) {
    var _a, _b, _c;
    // Receptor — bold/verbose format from resource_ops
    const receptorBold = body.match(/Receptor\s+(?:#\d+\s+)?[—–-]\s+statut\s*:\s*\*{0,2}([^*(\n]+)\*{0,2}\s*\(PDB\s+([^,)]+)(?:,\s*([^)]+))?\)/i);
    if (receptorBold) {
        const rows = [
            { label: 'PDB', value: receptorBold[2].trim() },
            { label: 'Statut', value: receptorBold[1].trim() },
        ];
        if (receptorBold[3])
            rows.push({ label: 'Binding sites', value: receptorBold[3].trim() });
        return {
            kind: 'check', resource: 'receptor',
            title: 'CHECK RECEPTOR', headline: '',
            status: normalizeStatus(receptorBold[1]),
            rows,
        };
    }
    // Receptor — simple format
    const receptorSimple = body.match(/(?:Check|check)\s+receptor\s+(?:#\d+\s+)?[—–-]\s*statut\s*:\s*([^\n.]+)(?:\.\s*(.+))?/i);
    if (receptorSimple) {
        const extra = (_a = receptorSimple[2]) === null || _a === void 0 ? void 0 : _a.trim();
        const rows = [];
        const pdb = extra === null || extra === void 0 ? void 0 : extra.match(/PDB\s*:\s*(\S+)/i);
        if (pdb)
            rows.push({ label: 'PDB', value: pdb[1] });
        rows.push({ label: 'Statut', value: receptorSimple[1].trim() });
        const sites = extra === null || extra === void 0 ? void 0 : extra.match(/(\d+)\s+binding site/i);
        if (sites)
            rows.push({ label: 'Binding sites', value: sites[1] });
        return {
            kind: 'check', resource: 'receptor',
            title: 'CHECK RECEPTOR', headline: '',
            status: normalizeStatus(receptorSimple[1]),
            rows,
        };
    }
    // Peptide check
    const peptideCheck = body.match(/(?:Check|check)\s+peptide\s+(?:#\d+\s+)?[—–-]\s*statut\s*:\s*([^\n.]+)/i);
    if (peptideCheck) {
        const rows = [];
        const seqMatch = body.match(/s[eé]quence\s*:\s*(\S+)/i);
        if (seqMatch)
            rows.push({ label: 'Séquence', value: seqMatch[1] });
        rows.push({ label: 'Statut', value: peptideCheck[1].trim() });
        return {
            kind: 'check', resource: 'peptide',
            title: 'CHECK PEPTIDE', headline: '',
            status: normalizeStatus(peptideCheck[1]),
            rows,
        };
    }
    // Delete receptor
    const deleteReceptor = body.match(/Récepteur\s+(?:#\d+\s+)?\(([^)]+)\)\s+supprimé|Suppression\s+du\s+récepteur\s+(?:PDB\s+)?([A-Z0-9]+)/i);
    if (deleteReceptor) {
        const pdb = (_c = ((_b = deleteReceptor[1]) !== null && _b !== void 0 ? _b : deleteReceptor[2])) === null || _c === void 0 ? void 0 : _c.trim();
        return {
            kind: 'delete', resource: 'receptor',
            title: 'DELETE RECEPTOR',
            headline: pdb ? `Récepteur supprimé (PDB : ${pdb})` : 'Récepteur supprimé',
            status: 'completed',
            rows: pdb ? [{ label: 'PDB', value: pdb }] : [],
        };
    }
    // Error line
    const errLine = body.match(/^Erreur\s+(check|stop|delete|relaunch)\s+(\w+)\s+#?(\S+)\s*:\s*(.+)$/i);
    if (errLine) {
        const res = errLine[2].toLowerCase();
        const resource = res.includes('peptide') ? 'peptide'
            : res.includes('docking') ? 'docking'
                : res.includes('receptor') ? 'receptor'
                    : 'generic';
        const op = errLine[1].toLowerCase();
        const notice = {
            kind: 'error', resource,
            title: 'INFO', headline: 'La requête n\'a pas abouti',
            status: 'failed',
            rows: [{ label: 'Détail', value: errLine[4].trim() }],
        };
        notice.title = resourceActionLabel(Object.assign(Object.assign({}, notice), { kind: op === 'delete' ? 'delete' : 'check' }));
        return notice;
    }
    return null;
}
function parseJobPending(body) {
    var _a, _b;
    if (!/n'est pas encore prêt|En attente d'une action MexaScreen/i.test(body))
        return null;
    const statusMatch = (_a = body.match(/statut\s*:\s*([^)]+)\)/i)) !== null && _a !== void 0 ? _a : body.match(/statut\s*:\s*([^\n.]+)/i);
    const isPeptide = /peptide/i.test(body);
    const isError = /échoué|failed/i.test(body);
    const resource = isPeptide ? 'peptide' : 'receptor';
    return {
        kind: isError ? 'error' : 'pending',
        resource,
        title: resource === 'peptide' ? 'PEPTIDE DESIGN' : 'RECEPTOR PREPARATION',
        headline: isError ? 'Échec — action requise' : 'Job en cours…',
        status: (_b = normalizeStatus(statusMatch === null || statusMatch === void 0 ? void 0 : statusMatch[1])) !== null && _b !== void 0 ? _b : (isError ? 'failed' : 'in_progress'),
        rows: [],
    };
}
function parseStepBlock(text) {
    var _a, _b, _c;
    const m = text.trim().match(STEP_BLOCK_RE);
    if (!m)
        return null;
    const stepTitle = m[1].trim();
    const body = m[2].trim();
    return ((_c = (_b = (_a = parseReceptorReuse(stepTitle, body)) !== null && _a !== void 0 ? _a : parseReceptorLaunch(stepTitle, body)) !== null && _b !== void 0 ? _b : parseResourceCheck(body)) !== null && _c !== void 0 ? _c : null);
}
function parsePlainOperational(text) {
    var _a, _b;
    const trimmed = text.trim();
    if (!trimmed)
        return null;
    return (_b = (_a = parseResourceCheck(trimmed)) !== null && _a !== void 0 ? _a : parseJobPending(trimmed)) !== null && _b !== void 0 ? _b : null;
}
function fromStructured(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const o = raw;
    const title = typeof o.title === 'string' ? o.title : '';
    const headline = typeof o.headline === 'string' ? o.headline : '';
    if (!title && !headline)
        return null;
    const rows = Array.isArray(o.rows)
        ? o.rows
            .filter((r) => !!r && typeof r === 'object')
            .map((r) => { var _a, _b; return ({ label: String((_a = r.label) !== null && _a !== void 0 ? _a : ''), value: String((_b = r.value) !== null && _b !== void 0 ? _b : '—') }); })
            .filter((r) => r.label)
        : [];
    const kind = ['reuse', 'launch', 'check', 'pending', 'error', 'info', 'delete'].includes(o.kind)
        ? o.kind
        : 'info';
    const resource = ['receptor', 'peptide', 'docking', 'binding_site', 'generic'].includes(o.resource)
        ? o.resource
        : 'generic';
    const notice = {
        kind,
        resource,
        title: title || headline,
        headline: sanitizeHeadline({ kind, resource, title, headline: headline || '', status: typeof o.status === 'string' ? o.status : undefined, rows: filterRows(rows) }),
        status: typeof o.status === 'string' ? o.status : undefined,
        rows: filterRows(rows),
    };
    // Always normalize the title from resource+kind so LangGraph generic titles are overridden.
    notice.title = resourceActionLabel(notice);
    return notice;
}
// ── Public API ────────────────────────────────────────────────────────────────
/** Prefer LangGraph `chat_notices[]`; fallback heuristics on ai_response until agent is updated. */
export function resolveChatNotices(data) {
    var _a, _b, _c, _d;
    const structured = data.chat_notices;
    if (Array.isArray(structured) && structured.length) {
        return structured.map(fromStructured).filter((n) => n != null);
    }
    const notices = [];
    const text = ((_b = (_a = data.ai_response) !== null && _a !== void 0 ? _a : data.message) !== null && _b !== void 0 ? _b : '').trim();
    const snapErr = (_c = data.state_snapshot) === null || _c === void 0 ? void 0 : _c.error_message;
    const err = (_d = data.error) !== null && _d !== void 0 ? _d : (typeof snapErr === 'string' ? snapErr : '');
    if (err) {
        notices.push({
            kind: 'error', resource: 'generic',
            title: 'INFO', headline: 'La requête n\'a pas abouti',
            status: 'failed',
            rows: [{ label: 'Détail', value: err }],
        });
    }
    const stepNotice = text ? parseStepBlock(text) : null;
    if (stepNotice) {
        notices.push(stepNotice);
        return notices;
    }
    const plain = text ? parsePlainOperational(text) : null;
    if (plain)
        notices.push(plain);
    return notices;
}
// Short redundant phrases to suppress when a notice card already shows the info.
const SUPPRESS_PHRASES_RE = /résultat\s+(du\s+)?check|résultat\s+du\s+contrôle|contrôle\s+ci-dessus|ci-dessus|voir\s+(la\s+)?carte|voir\s+ci-dessus/i;
/** When notices carry the operational content, avoid duplicating long Step N block in a bubble. */
export function shouldSuppressOperationalBubble(data, notices) {
    var _a;
    if (!notices.length)
        return false;
    const structured = data.chat_notices;
    if (Array.isArray(structured) && structured.length)
        return true;
    const text = ((_a = data.ai_response) !== null && _a !== void 0 ? _a : '').trim();
    if (STEP_BLOCK_RE.test(text))
        return true;
    if (parsePlainOperational(text))
        return true;
    if (SUPPRESS_PHRASES_RE.test(text))
        return true;
    if (data.error)
        return true;
    return false;
}
export function appendChatNotice(notice) {
    const chatHistory = document.getElementById('chat-history');
    if (!chatHistory)
        return;
    const normalized = Object.assign(Object.assign({}, notice), { headline: sanitizeHeadline(notice) });
    const colorClass = noticeColorClass(normalized);
    const resourceLabel = resourceActionLabel(normalized);
    const pill = normalized.status
        ? `<span class="chat-notice-status-pill ${statusPillClass(normalized.status)}">${escapeHtml(normalized.status)}</span>`
        : '';
    const headlineHtml = normalized.headline
        ? `<p class="chat-notice-headline">${escapeHtml(normalized.headline)}</p>`
        : '';
    const rowsHtml = normalized.rows.length
        ? `<div class="chat-notice-body">${notice.rows.map(r => `<div class="chat-notice-row">
          <span class="chat-notice-row-label">${escapeHtml(r.label)}</span>
          <strong class="chat-notice-row-value">${escapeHtml(r.value)}</strong>
        </div>`).join('')}</div>`
        : '';
    const block = document.createElement('div');
    block.className = `chat-resource-notice ${colorClass}`;
    block.dataset.noticeKind = normalized.kind;
    block.dataset.noticeResource = normalized.resource;
    block.innerHTML = `
    <div class="chat-notice-header">
      <span class="chat-notice-resource-title">${escapeHtml(resourceLabel)}</span>
      ${pill}
    </div>
    ${headlineHtml}
    ${rowsHtml}
  `;
    chatHistory.appendChild(block);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
export function appendChatNotices(notices) {
    notices.forEach(appendChatNotice);
}
